import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Equipment,
  CreateEquipmentDto,
  UpdateEquipmentDto,
  EquipmentFilters,
  EquipmentResponse,
  EquipmentStats,
  MaintenanceRecord,
  equipmentApi,
  createEquipmentPurchaseTransaction,
} from '../api/equipment';
import { suppliersApi } from '../api/suppliers';

interface UseEquipmentReturn {
  // Data
  equipment: Equipment[];
  statistics: EquipmentStats | null;
  selectedEquipment: Equipment | null;
  
  // Loading states
  loading: boolean;
  statisticsLoading: boolean;
  actionLoading: boolean;
  
  // Error states
  error: string | null;
  
  // Pagination
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  
  // Actions
  loadEquipment: (filters?: EquipmentFilters) => Promise<void>;
  loadStatistics: () => Promise<void>;
  createEquipment: (data: CreateEquipmentDto) => Promise<Equipment>;
  updateEquipment: (id: string, data: UpdateEquipmentDto) => Promise<Equipment>;
  deleteEquipment: (id: string) => Promise<void>;
  getEquipmentById: (id: string) => Promise<Equipment>;
  recordMaintenance: (data: MaintenanceRecord) => Promise<Equipment>;
  getMaintenanceDue: () => Promise<Equipment[]>;
  getMaintenanceOverdue: () => Promise<Equipment[]>;
  getUnderWarranty: () => Promise<Equipment[]>;
  refreshData: () => Promise<void>;
  setSelectedEquipment: (equipment: Equipment | null) => void;
  clearError: () => void;
}

export const useEquipment = (initialFilters: EquipmentFilters = {}): UseEquipmentReturn => {
  // State
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [statistics, setStatistics] = useState<EquipmentStats | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [loading, setLoading] = useState(false);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<EquipmentFilters>(initialFilters);
  const currentFiltersRef = useRef<EquipmentFilters>(initialFilters);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Update ref when filters change
  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    try {
      setStatisticsLoading(true);
      const stats = await equipmentApi.getStatistics();
      setStatistics(stats);
    } catch (err: any) {
      console.error('Error loading statistics:', err);
      // Don't set error for statistics as it's not critical
    } finally {
      setStatisticsLoading(false);
    }
  }, []);

  // Load equipment with filters
  const loadEquipment = useCallback(async (filters: EquipmentFilters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const mergedFilters = { ...currentFiltersRef.current, ...filters };
      setCurrentFilters(mergedFilters);
      
      const response: EquipmentResponse = await equipmentApi.getAll(mergedFilters);
      
      setEquipment(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load equipment');
      console.error('Error loading equipment:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create stable refs for the functions
  const loadEquipmentRef = useRef(loadEquipment);
  const loadStatisticsRef = useRef(loadStatistics);

  useEffect(() => {
    loadEquipmentRef.current = loadEquipment;
    loadStatisticsRef.current = loadStatistics;
  }, [loadEquipment, loadStatistics]);

  // Create equipment
  const createEquipment = useCallback(async (data: CreateEquipmentDto): Promise<Equipment> => {
    try {
      setActionLoading(true);
      setError(null);
      
      const newEquipment = await equipmentApi.create(data);
      
      // Create purchase transaction if purchase information is provided
      if (data.purchasePrice && data.purchasePrice > 0 && data.purchaseDate) {
        try {
          let supplierInfo = {
            name: data.supplier || 'Unknown Supplier',
            phone: '',
            address: '',
          };

          // If supplier ID is provided, get supplier details
          if (data.supplierId) {
            try {
              const supplier = await suppliersApi.getSupplier(data.supplierId);
              supplierInfo = {
                name: supplier.name,
                phone: supplier.phone || '',
                address: supplier.fullAddress || '',
              };
            } catch (supplierErr) {
            }
          }

          await createEquipmentPurchaseTransaction(
            newEquipment.id,
            data.purchasePrice,
            data.supplierId,
            supplierInfo.name,
            supplierInfo.phone,
            supplierInfo.address,
            'CASH', // Default payment method
            data.purchaseDate,
            `Equipment purchase: ${newEquipment.name}`
          );
          
        } catch (transactionErr) {
          console.error('Failed to create purchase transaction:', transactionErr);
          // Don't fail the equipment creation if transaction fails
        }
      }
      
      // Refresh the list
      await loadEquipmentRef.current();
      await loadStatisticsRef.current();
      
      return newEquipment;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to create equipment';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Update equipment
  const updateEquipment = useCallback(async (id: string, data: UpdateEquipmentDto): Promise<Equipment> => {
    try {
      setActionLoading(true);
      setError(null);
      
      const updatedEquipment = await equipmentApi.update(id, data);
      
      // Update the equipment in the list
      setEquipment(prev => prev.map(item => 
        item.id === id ? updatedEquipment : item
      ));
      
      // Update selected equipment if it's the one being updated
      if (selectedEquipment?.id === id) {
        setSelectedEquipment(updatedEquipment);
      }
      
      await loadStatisticsRef.current();
      
      return updatedEquipment;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update equipment';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [selectedEquipment]);

  // Delete equipment
  const deleteEquipment = useCallback(async (id: string): Promise<void> => {
    try {
      setActionLoading(true);
      setError(null);
      
      await equipmentApi.delete(id);
      
      // Remove from the list
      setEquipment(prev => prev.filter(item => item.id !== id));
      
      // Clear selected equipment if it's the one being deleted
      if (selectedEquipment?.id === id) {
        setSelectedEquipment(null);
      }
      
      await loadStatisticsRef.current();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to delete equipment';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [selectedEquipment]);

  // Get equipment by ID
  const getEquipmentById = useCallback(async (id: string): Promise<Equipment> => {
    try {
      setActionLoading(true);
      setError(null);
      
      const equipment = await equipmentApi.getById(id);
      setSelectedEquipment(equipment);
      
      return equipment;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to get equipment';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Record maintenance
  const recordMaintenance = useCallback(async (data: MaintenanceRecord): Promise<Equipment> => {
    try {
      setActionLoading(true);
      setError(null);
      
      const updatedEquipment = await equipmentApi.recordMaintenance(data);
      
      // Update the equipment in the list
      setEquipment(prev => prev.map(item => 
        item.id === data.equipmentId ? updatedEquipment : item
      ));
      
      // Update selected equipment if it's the one being updated
      if (selectedEquipment?.id === data.equipmentId) {
        setSelectedEquipment(updatedEquipment);
      }
      
      await loadStatisticsRef.current();
      
      return updatedEquipment;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to record maintenance';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [selectedEquipment]);

  // Get equipment with maintenance due
  const getMaintenanceDue = useCallback(async (): Promise<Equipment[]> => {
    try {
      return await equipmentApi.getMaintenanceDue();
    } catch (err: any) {
      console.error('Error getting maintenance due equipment:', err);
      return [];
    }
  }, []);

  // Get equipment with overdue maintenance
  const getMaintenanceOverdue = useCallback(async (): Promise<Equipment[]> => {
    try {
      return await equipmentApi.getMaintenanceOverdue();
    } catch (err: any) {
      console.error('Error getting overdue maintenance equipment:', err);
      return [];
    }
  }, []);

  // Get equipment under warranty
  const getUnderWarranty = useCallback(async (): Promise<Equipment[]> => {
    try {
      return await equipmentApi.getUnderWarranty();
    } catch (err: any) {
      console.error('Error getting equipment under warranty:', err);
      return [];
    }
  }, []);

  // Refresh all data
  const refreshData = useCallback(async () => {
    await Promise.all([
      loadEquipmentRef.current(),
      loadStatisticsRef.current(),
    ]);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load initial data
  useEffect(() => {
    loadEquipment();
    loadStatistics();
  }, []);

  return {
    // Data
    equipment,
    statistics,
    selectedEquipment,
    
    // Loading states
    loading,
    statisticsLoading,
    actionLoading,
    
    // Error states
    error,
    
    // Pagination
    pagination,
    
    // Actions
    loadEquipment,
    loadStatistics,
    createEquipment,
    updateEquipment,
    deleteEquipment,
    getEquipmentById,
    recordMaintenance,
    getMaintenanceDue,
    getMaintenanceOverdue,
    getUnderWarranty,
    refreshData,
    setSelectedEquipment,
    clearError,
  };
}; 